/**
 * Cliente da API. O access token fica só em memória; a renovação usa o cookie httpOnly
 * (POST /api/auth/refresh), então recarregar a página mantém a sessão sem guardar token no navegador.
 */

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  roles: string[];
  permissions: string[];
  /** Pode transferir atendimentos que estão com outros operadores (opção do cadastro). */
  canTransferOthers?: boolean;
}

interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  user: AuthUser;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let expiresAt = 0;
let refreshing: Promise<LoginResponse | null> | null = null;
const listeners = new Set<(user: AuthUser | null) => void>();

/** Avisa quem depende da sessão (tela, socket) quando ela muda ou cai. */
export function onSessionChange(fn: (user: AuthUser | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function setSession(session: LoginResponse | null): void {
  accessToken = session?.accessToken ?? null;
  expiresAt = session ? Date.now() + session.expiresIn * 1000 : 0;
  for (const fn of listeners) fn(session?.user ?? null);
}

async function readError(res: Response): Promise<ApiError> {
  let body: { error?: string; message?: string; details?: unknown } = {};
  try {
    body = await res.json();
  } catch {
    // sem corpo JSON
  }
  const fallback = res.status === 503 ? 'Agidesk indisponível no momento' : `Erro ${res.status}`;
  return new ApiError(res.status, body.error ?? String(res.status), body.message ?? fallback, body.details);
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw await readError(res);
  const session = (await res.json()) as LoginResponse;
  setSession(session);
  return session.user;
}

/** Renova pelo cookie. Várias chamadas simultâneas compartilham a mesma renovação. */
export function refreshSession(): Promise<LoginResponse | null> {
  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' });
      if (!res.ok) {
        setSession(null);
        return null;
      }
      const session = (await res.json()) as LoginResponse;
      setSession(session);
      return session;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  } finally {
    setSession(null);
  }
}

/** Token válido para o socket (renova se estiver perto de expirar). */
export async function currentToken(): Promise<string | null> {
  if (accessToken && Date.now() < expiresAt - 30_000) return accessToken;
  return (await refreshSession())?.accessToken ?? null;
}

async function send(method: string, path: string, body: unknown, retry: boolean): Promise<Response> {
  const token = await currentToken();
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && retry && (await refreshSession())) return send(method, path, body, false);
  return res;
}

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await send(method, path, body, true);
  if (!res.ok) throw await readError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T = void>(path: string, body?: unknown) => api<T>('POST', path, body ?? {});
export const put = <T = void>(path: string, body?: unknown) => api<T>('PUT', path, body ?? {});
export const patch = <T = void>(path: string, body?: unknown) => api<T>('PATCH', path, body ?? {});
export const del = <T = void>(path: string) => api<T>('DELETE', path);

/** Baixa um arquivo protegido como texto (ex.: animação de figurinha). */
export async function fetchText(path: string): Promise<string> {
  const res = await send('GET', path, undefined, true);
  if (!res.ok) throw await readError(res);
  return res.text();
}

/** Baixa um arquivo protegido (mídia das conversas) e devolve um endereço local (blob:). */
export async function fetchBlobUrl(path: string): Promise<string> {
  const res = await send('GET', path, undefined, true);
  if (!res.ok) throw await readError(res);
  return URL.createObjectURL(await res.blob());
}

export function hasPermission(user: AuthUser | null, permission: string): boolean {
  if (!user) return false;
  const [resource] = permission.split(':');
  return user.permissions.includes('*') || user.permissions.includes(permission) || user.permissions.includes(`${resource}:*`);
}

export const errorMessage = (err: unknown) => (err instanceof Error ? err.message : 'Erro inesperado');
